/**
 * Cell-level parsing shared by the .xlsx/.csv import pipelines (sales, manufactures).
 * Pure: no I/O, no UI.
 */

import { type ImportCell } from './importFileParser'

/** A calendar date (+ optional wall-clock time) exactly as written in the file. */
export interface ImportDate {
  year: number
  /** 1-12 */
  month: number
  /** 1-31 */
  day: number
  hasTime: boolean
  hour: number
  minute: number
  second: number
}

/** Days between the 1900 and the 1904 Excel date systems. */
const DATE_SYSTEM_OFFSET_DAYS = 1462
/** Excel serial of 1970-01-01 in the 1900 system (serial 25569). */
const UNIX_EPOCH_SERIAL = 25569
const MS_PER_DAY = 86400000

/**
 * Serial numbers only appear in the date column, where a bare number cannot mean anything
 * else. The bounds keep an obviously non-date number (a year like 2026, a phone number)
 * from silently decoding into a date: ~1954-01-01 .. ~2119-12-31.
 */
const MIN_DATE_SERIAL = 20000
const MAX_DATE_SERIAL = 80000

export const cellToString = (cell: ImportCell): string => {
  if (cell === null || cell === undefined) return ''
  if (typeof cell === 'number') {
    // Avoid exponential notation for long numeric articles/barcodes.
    return Number.isInteger(cell) ? cell.toFixed(0) : String(cell)
  }
  return String(cell).trim()
}

const isRealDate = (year: number, month: number, day: number): boolean => {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false
  const probe = new Date(Date.UTC(year, month - 1, day))
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  )
}

const makeDate = (
  year: number,
  month: number,
  day: number,
  time: { hour: number; minute: number; second: number } | null
): ImportDate | null => {
  if (!isRealDate(year, month, day)) return null
  if (time && (time.hour > 23 || time.minute > 59 || time.second > 59)) return null
  return {
    year,
    month,
    day,
    hasTime: time !== null,
    hour: time?.hour ?? 0,
    minute: time?.minute ?? 0,
    second: time?.second ?? 0,
  }
}

/**
 * Decode an Excel date serial without going through the local timezone: the integer part
 * is days since the epoch, the fraction is the wall-clock time of day.
 */
const parseDateSerial = (serial: number, date1904: boolean): ImportDate | null => {
  const serial1900 = date1904 ? serial + DATE_SYSTEM_OFFSET_DAYS : serial
  if (serial1900 < MIN_DATE_SERIAL || serial1900 > MAX_DATE_SERIAL) return null

  const days = Math.floor(serial1900)
  // Round to the nearest second: Excel stores 12:00 as .5 but 09:30 as .3958333…
  const secondsOfDay = Math.round((serial1900 - days) * 86400)
  const utc = new Date((days - UNIX_EPOCH_SERIAL) * MS_PER_DAY)

  const hasTime = secondsOfDay > 0
  return makeDate(
    utc.getUTCFullYear(),
    utc.getUTCMonth() + 1,
    utc.getUTCDate(),
    hasTime
      ? {
          hour: Math.floor(secondsOfDay / 3600),
          minute: Math.floor((secondsOfDay % 3600) / 60),
          second: secondsOfDay % 60,
        }
      : null
  )
}

const TIME_PATTERN = '(?:[ T](\\d{1,2}):(\\d{2})(?::(\\d{2}))?)?'
// Day-first (ru locale): 31.12.2026, 31/12/2026, 31-12-2026
const DAY_FIRST = new RegExp(`^(\\d{1,2})[.\\-/](\\d{1,2})[.\\-/](\\d{4})${TIME_PATTERN}$`)
// ISO-ish: 2026-12-31, 2026/12/31
const YEAR_FIRST = new RegExp(`^(\\d{4})[.\\-/](\\d{1,2})[.\\-/](\\d{1,2})${TIME_PATTERN}$`)

const timeFromMatch = (match: RegExpMatchArray, offset: number) => {
  if (match[offset] === undefined) return null
  return {
    hour: parseInt(match[offset], 10),
    minute: parseInt(match[offset + 1], 10),
    second: match[offset + 2] === undefined ? 0 : parseInt(match[offset + 2], 10),
  }
}

/**
 * Parse a date cell. Accepts Excel serials and the string forms `dd.MM.yyyy`,
 * `dd/MM/yyyy`, `dd-MM-yyyy`, `yyyy-MM-dd`, `yyyy/MM/dd`, each with an optional
 * `HH:mm` / `HH:mm:ss`. Dotted and slashed forms are read DAY-first (ru locale).
 * Returns null when the cell cannot be understood — never a guess.
 */
export const parseImportDate = (cell: ImportCell, date1904 = false): ImportDate | null => {
  if (typeof cell === 'number') return parseDateSerial(cell, date1904)

  const text = cellToString(cell)
  if (!text) return null

  // A pure number that arrived as text (some CSV exports quote everything).
  if (/^\d+(\.\d+)?$/.test(text)) {
    return parseDateSerial(parseFloat(text), date1904)
  }

  const yearFirst = text.match(YEAR_FIRST)
  if (yearFirst) {
    return makeDate(
      parseInt(yearFirst[1], 10),
      parseInt(yearFirst[2], 10),
      parseInt(yearFirst[3], 10),
      timeFromMatch(yearFirst, 4)
    )
  }

  const dayFirst = text.match(DAY_FIRST)
  if (dayFirst) {
    return makeDate(
      parseInt(dayFirst[3], 10),
      parseInt(dayFirst[2], 10),
      parseInt(dayFirst[1], 10),
      timeFromMatch(dayFirst, 4)
    )
  }

  return null
}

/**
 * Parse a quantity cell. Accepts fractional values (weight goods) and both decimal
 * separators. Returns null when the cell is not a usable positive number.
 */
export const parseImportQuantity = (cell: ImportCell): number | null => {
  if (typeof cell === 'number') return Number.isFinite(cell) && cell > 0 ? cell : null

  const text = cellToString(cell).replace(/\s/g, '').replace(',', '.')
  if (!text) return null
  if (!/^\d*\.?\d+$/.test(text)) return null

  const quantity = parseFloat(text)
  return Number.isFinite(quantity) && quantity > 0 ? quantity : null
}

export const readCell = (row: ImportCell[], index: number | null): ImportCell =>
  index === null || index < 0 ? null : (row[index] ?? null)
